import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Boolean control on the OneVedAI checkbox. Works with formControlName, [formControl] and [(ngModel)] / [checked]. */
@Component({
  selector: 'app-checkbox',
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppCheckboxComponent), multi: true }],
  template: `
    <label class="check">
      <onevedai-checkbox [checked]="!!checked" [disabled]="disabled" [indeterminate]="indeterminate" (checkedChange)="onToggle($event)"></onevedai-checkbox>
      <span class="check-label"><ng-content></ng-content></span>
    </label>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }
      .check {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        cursor: pointer;
        font: var(--text-body-md);
      }
    `,
  ],
})
export class AppCheckboxComponent implements ControlValueAccessor {
  @Input() checked: boolean | null | undefined = false;
  @Input() indeterminate = false;
  @Input() disabled = false;
  @Output() checkedChange = new EventEmitter<boolean>();
  /** MatCheckbox API compatibility: `(change)="f($event.checked)"`. */
  @Output() change = new EventEmitter<{ checked: boolean }>();

  private onChange: (v: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  onToggle(value: boolean): void {
    this.checked = value;
    this.onChange(value);
    this.onTouched();
    this.checkedChange.emit(value);
    this.change.emit({ checked: value });
  }

  writeValue(value: unknown): void {
    this.checked = !!value;
  }

  registerOnChange(fn: (v: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

/** On/off switch on the OneVedAI toggle (replacement for mat-slide-toggle). */
@Component({
  selector: 'app-toggle',
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppToggleComponent), multi: true }],
  template: `
    <label class="tog">
      <onevedai-toggle [checked]="!!checked" [disabled]="disabled" (checkedChange)="onToggle($event)"></onevedai-toggle>
      <span class="tog-label"><ng-content></ng-content></span>
    </label>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }
      .tog {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        cursor: pointer;
        font: var(--text-body-md);
      }
    `,
  ],
})
export class AppToggleComponent implements ControlValueAccessor {
  @Input() checked: boolean | null | undefined = false;
  @Input() disabled = false;
  @Output() checkedChange = new EventEmitter<boolean>();
  /** MatSlideToggle API compatibility: `(change)="f($event.checked)"`. */
  @Output() change = new EventEmitter<{ checked: boolean }>();

  private onChange: (v: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  onToggle(value: boolean): void {
    this.checked = value;
    this.onChange(value);
    this.onTouched();
    this.checkedChange.emit(value);
    this.change.emit({ checked: value });
  }

  writeValue(value: unknown): void {
    this.checked = !!value;
  }

  registerOnChange(fn: (v: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
